'use client'

import React from 'react'

import { HiExclamation, HiInformationCircle } from 'react-icons/hi'

import { Box, Flex, Text } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { getInfoBannerTokens } from './InfoBanner.styles'
import type { InfoBannerTone } from './InfoBanner.styles'

export interface InfoBannerIProps {
  title: string
  /** Supporting copy below the title. Omit it for a single-line banner — the
   * title then sits inline with the icon rather than above an empty row. */
  description?: string
  /** 'warning' renders the amber palette and the exclamation icon. */
  tone?: InfoBannerTone
  colorScheme?: ColorScheme
}

const TONE_ICONS: Record<InfoBannerTone, typeof HiInformationCircle> = {
  info: HiInformationCircle,
  warning: HiExclamation,
}

export const InfoBanner = ({
  title,
  description,
  tone = 'info',
  colorScheme = 'dark',
}: InfoBannerIProps) => {
  const t = getInfoBannerTokens(colorScheme, tone)
  const ToneIcon = TONE_ICONS[tone]

  return (
    <Box bg={t.bg} borderRadius='8px' p='16px'>
      {description && (
        <Text
          color={t.titleColor}
          fontSize='14px'
          fontWeight='700'
          mb='4px'
          pl='28px'
        >
          {title}
        </Text>
      )}
      <Flex alignItems='flex-start' gap='8px'>
        <Box color={t.iconColor} flexShrink={0}>
          <ToneIcon size='20px' />
        </Box>
        <Text
          color={description ? t.contentColor : t.titleColor}
          fontSize='14px'
          fontWeight={description ? '400' : '700'}
          lineHeight='1.5'
        >
          {description ?? title}
        </Text>
      </Flex>
    </Box>
  )
}
